import { get, toString } from 'es-toolkit/compat';
import {
  applyStainTypeIfRequired,
  getFormattedArea,
  getFormattedCellWithoutPositiveOrNegative,
  getFormattedCellWithStainAndClassifiers,
  getFormattedFeatureParam,
  getFormattedStain,
  getFormattedText,
  getGridBasedString,
  getStatistic,
} from '..';
import { FormatBracketsOptions } from '../formatBracketsOptions';
import { TemplateSegment } from './types';

export const evaluateSegments = (
  segments: TemplateSegment[],
  dictBrackets: Record<string, any>,
  options: FormatBracketsOptions
): string => {
  let result = '';

  for (const segment of segments) {
    switch (segment.type) {
      case 'lit':
        result += segment.v;
        break;

      case 'cell':
        result += getFormattedCellWithStainAndClassifiers(
          get(dictBrackets, segment.c),
          get(dictBrackets, segment.s),
          get(dictBrackets, segment.cls),
          options
        );
        break;

      case 'cell_stripped':
        result += getFormattedCellWithStainAndClassifiers(
          getFormattedCellWithoutPositiveOrNegative(get(dictBrackets, segment.c)),
          get(dictBrackets, segment.s),
          get(dictBrackets, segment.cls),
          options
        );
        break;

      case 'area':
        result += getFormattedArea(get(dictBrackets, segment.path), options.areaTypeOptions);
        break;

      case 'raw':
        result += toString(get(dictBrackets, segment.path));
        break;

      case 'stain':
        result += getFormattedStain(
          get(dictBrackets, segment.path),
          options.stainTypeOptions,
          options.rnaProbeDisplayNameOverridesByProbeId
        );
        break;

      case 'param':
        result += getFormattedFeatureParam(get(dictBrackets, segment.path), options.featureParams);
        break;

      case 'text':
        result += getFormattedText(get(dictBrackets, segment.path));
        break;

      case 'grid_prefix': {
        const { startWith } = getGridBasedString(dictBrackets, options.featureParams);
        result += startWith;
        break;
      }

      case 'grid_suffix': {
        const { endWith } = getGridBasedString(dictBrackets, options.featureParams);
        result += endWith;
        break;
      }

      case 'grid_threshold': {
        const { threshold } = getGridBasedString(dictBrackets, options.featureParams);
        result += threshold;
        break;
      }

      case 'statistic':
        result += getStatistic(get(dictBrackets, segment.path));
        break;

      case 'optional':
        if (get(dictBrackets, segment.condition)) {
          result += evaluateSegments(segment.segments, dictBrackets, options);
        }
        break;
    }
  }

  return result;
};

export const evaluateWithStain = (
  segments: TemplateSegment[],
  dictBrackets: Record<string, any>,
  options: FormatBracketsOptions,
  applyStain: boolean
): string => {
  const formatted = evaluateSegments(segments, dictBrackets, options);
  if (applyStain) {
    return applyStainTypeIfRequired(formatted, dictBrackets['s'], options);
  }
  return formatted;
};
